import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  History, Undo2, AlertTriangle, ShieldCheck, X, Loader2, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/adminApi";

/**
 * BulkUploadHistoryModal — lists prior bulk-CSV uploads for the current
 * sub-tab, with a one-click Revert flow.
 *
 * Backend contract:
 *   GET  /admin/jurisdictions/bulk-upload-events?collection=<tax|privacy>
 *   GET  /admin/jurisdictions/bulk-upload-revert-preview/{event_id}
 *   POST /admin/jurisdictions/bulk-upload-revert/{event_id}
 *        { mode: 'skip_admin_edited' | 'force_all', confirm_force?: bool }
 *
 * SoT protection: rows edited manually AFTER the upload are shown as SoT
 * conflicts and skipped by default. Force mode overrides — requires typing
 * FORCE, same gate as the original bulk upload flow.
 */
export default function BulkUploadHistoryModal({ open, onClose, collection, onReverted }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);           // full event row
  const [preview, setPreview] = useState(null);              // revert preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [forceInput, setForceInput] = useState("");

  const loadEvents = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/jurisdictions/bulk-upload-events", {
        params: { collection },
      });
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load bulk upload history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelected(null);
      setPreview(null);
      setShowForceConfirm(false);
      setForceInput("");
      loadEvents();
    }
  }, [open, collection]);

  const openRevert = async (evt) => {
    setSelected(evt);
    setPreview(null);
    setShowForceConfirm(false);
    setForceInput("");
    setPreviewLoading(true);
    try {
      const { data } = await adminApi.get(
        `/admin/jurisdictions/bulk-upload-revert-preview/${evt.id}`,
      );
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setSelected(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const doRevert = async (mode) => {
    if (!selected) return;
    if (mode === "force_all" && forceInput.trim().toUpperCase() !== "FORCE") {
      toast.error("Type FORCE to confirm the override");
      return;
    }
    setReverting(true);
    try {
      const { data } = await adminApi.post(
        `/admin/jurisdictions/bulk-upload-revert/${selected.id}`,
        { mode, confirm_force: mode === "force_all" },
      );
      toast.success(
        `Reverted: ${data.updated_count} row(s), ${data.skipped_sot_count} SoT-skipped, ${data.rejected_count} missing`,
      );
      setSelected(null);
      setPreview(null);
      setShowForceConfirm(false);
      setForceInput("");
      onReverted?.();
      await loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally {
      setReverting(false);
    }
  };

  const close = () => {
    setSelected(null);
    setPreview(null);
    setShowForceConfirm(false);
    setForceInput("");
    onClose();
  };

  const s = preview?.summary;
  const canSkipRevert = s && s.will_revert > 0;
  const hasSotConflicts = s && s.sot_conflicts > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !reverting && close()}>
      <DialogContent className="max-w-4xl bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
        <DialogHeader>
          <DialogTitle className="text-[var(--rg-text-strong)] flex items-center gap-2">
            <History className="w-5 h-5" />
            Bulk Upload History — {collection === "tax" ? "Sales Tax" : "Privacy Law"}
          </DialogTitle>
          <DialogDescription className="text-[var(--rg-text)] text-xs">
            View past CSV uploads and one-click revert any prior apply. Rows edited after the upload are protected by source-of-truth guards.
          </DialogDescription>
        </DialogHeader>

        {/* -------- LIST VIEW -------- */}
        {!selected && (
          <div className="max-h-[65vh] overflow-y-auto" data-testid="bulk-history-list">
            {loading && (
              <div className="py-8 text-center text-[var(--rg-muted)] text-sm">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…
              </div>
            )}
            {!loading && events.length === 0 && (
              <div className="py-8 text-center text-sm text-[var(--rg-muted)]" data-testid="bulk-history-empty">
                No bulk uploads recorded for {collection === "tax" ? "Sales Tax" : "Privacy Law"} yet.
              </div>
            )}
            {!loading && events.map((e) => (
              <HistoryRow key={e.id} evt={e} onRevert={() => openRevert(e)} />
            ))}
          </div>
        )}

        {/* -------- REVERT PREVIEW -------- */}
        {selected && (
          <div className="max-h-[65vh] overflow-y-auto">
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 mb-4">
              <div className="text-xs text-[var(--rg-text)] font-mono">
                Reverting event <span className="text-[var(--rg-text-strong)]">{selected.id.slice(0, 8)}…</span>{" "}
                from <span className="text-[var(--rg-text-strong)]">{(selected.timestamp || "").slice(0, 19)}</span>
                {selected.admin_email && <> · applied by <span className="text-[var(--rg-text-strong)]">{selected.admin_email}</span></>}
              </div>
              <div className="text-xs text-[var(--rg-muted)] font-mono mt-1">
                {selected.updated_count} row(s) updated in the original apply
              </div>
            </div>

            {previewLoading && (
              <div className="py-8 text-center text-[var(--rg-muted)] text-sm">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Building revert plan…
              </div>
            )}

            {preview && (
              <>
                <RevertSummaryBar summary={preview.summary} />
                <div className="mt-4 space-y-2" data-testid="bulk-revert-plan-list">
                  {preview.plan.filter((p) => p.action !== "no_change").map((p) => (
                    <RevertRow key={p.abbreviation} plan={p} />
                  ))}
                  {preview.plan.every((p) => p.action === "no_change") && (
                    <div className="text-center text-sm text-[var(--rg-muted)] py-6" data-testid="bulk-revert-nothing">
                      Nothing to revert — all affected rows already match their pre-upload values.
                    </div>
                  )}
                </div>

                {showForceConfirm && (
                  <div className="mt-6 rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-4">
                    <p className="text-[var(--rg-red)] font-semibold text-sm">
                      <AlertTriangle className="w-4 h-4 inline mr-1.5" />
                      This will overwrite {s.sot_conflicts} row(s) that were edited AFTER this upload. Type{" "}
                      <code className="font-mono bg-black/30 px-1">FORCE</code> to confirm.
                    </p>
                    <Input
                      value={forceInput}
                      onChange={(e) => setForceInput(e.target.value)}
                      placeholder="FORCE"
                      className="mt-3 bg-[var(--rg-bg)] border-[var(--rg-red)] text-[var(--rg-text-strong)]"
                      data-testid="bulk-revert-force-input"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-[var(--rg-border)] pt-4">
          <div className="flex flex-wrap gap-2 w-full justify-end">
            {selected ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setSelected(null); setPreview(null); setShowForceConfirm(false); setForceInput(""); }}
                  disabled={reverting}
                  className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
                  data-testid="bulk-revert-back"
                >
                  Back
                </Button>
                {preview && !showForceConfirm && (
                  <>
                    <Button
                      onClick={() => doRevert("skip_admin_edited")}
                      disabled={!canSkipRevert || reverting}
                      className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
                      data-testid="bulk-revert-skip"
                    >
                      {reverting
                        ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        : <ShieldCheck className="w-4 h-4 mr-1.5" />}
                      Revert — skip admin-edited ({s ? s.will_revert : 0})
                    </Button>
                    {hasSotConflicts && (
                      <Button
                        onClick={() => setShowForceConfirm(true)}
                        variant="outline"
                        className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)]"
                        data-testid="bulk-revert-force-toggle"
                      >
                        <AlertTriangle className="w-4 h-4 mr-1.5" />
                        Force revert ALL ({s.sot_conflicts} admin-edited)
                      </Button>
                    )}
                  </>
                )}
                {preview && showForceConfirm && (
                  <Button
                    onClick={() => doRevert("force_all")}
                    disabled={forceInput.trim().toUpperCase() !== "FORCE" || reverting}
                    className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
                    data-testid="bulk-revert-force-apply"
                  >
                    {reverting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1.5" />}
                    Force revert ({s.will_revert + s.sot_conflicts} rows)
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="outline"
                onClick={close}
                className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
                data-testid="bulk-history-close"
              >
                <X className="w-4 h-4 mr-1.5" /> Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryRow({ evt, onRevert }) {
  const [expanded, setExpanded] = useState(false);
  const isRevert = evt.action === "revert";
  const isReverted = !!evt.reverted_by;
  const borderColor = isRevert ? "var(--rg-amber)" : (isReverted ? "var(--rg-muted)" : "var(--rg-sage)");
  return (
    <div
      className="rounded-sm border p-3 bg-[var(--rg-surface)] mb-2"
      style={{ borderColor }}
      data-testid={`bulk-history-row-${evt.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]"
              data-testid={`bulk-history-toggle-${evt.id}`}
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <span className="text-[var(--rg-text-strong)] font-mono text-xs">{(evt.timestamp || "").slice(0, 19)}</span>
            {isRevert ? (
              <span
                className="text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm"
                style={{ color: "var(--rg-amber)", borderColor: "var(--rg-amber)", background: "var(--rg-amber-soft)" }}
              >
                Revert
              </span>
            ) : (
              <span
                className="text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm"
                style={{ color: "var(--rg-sage)", borderColor: "var(--rg-sage)", background: "var(--rg-sage-soft)" }}
              >
                Apply
              </span>
            )}
            {isReverted && (
              <span
                className="text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm"
                style={{ color: "var(--rg-muted)", borderColor: "var(--rg-muted)", background: "var(--rg-surface-2)" }}
                title={`Superseded by revert ${(evt.reverted_by || "").slice(0, 8)}…`}
              >
                Reverted
              </span>
            )}
            {evt.mode === "force_all" && (
              <span
                className="text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm"
                style={{ color: "var(--rg-red)", borderColor: "var(--rg-red)", background: "var(--rg-red-soft)" }}
              >
                Force
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--rg-muted)] mt-1 font-mono">
            {evt.admin_email || "unknown"} · {evt.updated_count} updated ·{" "}
            {evt.skipped_sot_count} SoT-skipped · {evt.rejected_count} rejected
          </div>
        </div>
        <div className="flex-shrink-0">
          {evt.can_revert ? (
            <Button
              size="sm"
              onClick={onRevert}
              className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold"
              data-testid={`bulk-history-revert-${evt.id}`}
            >
              <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Revert
            </Button>
          ) : (
            <span
              className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)] font-mono"
              title={
                isRevert
                  ? "Revert events cannot themselves be reverted."
                  : isReverted
                    ? "Already reverted."
                    : "Missing per-row history — this event pre-dates the revert feature."
              }
            >
              Not revertible
            </span>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 border-t border-[var(--rg-border)] pt-3 space-y-1">
          {(evt.updated || []).length === 0 && (
            <div className="text-xs text-[var(--rg-muted)] italic">No row-level updates recorded.</div>
          )}
          {(evt.updated || []).map((u, i) => (
            <div key={i} className="font-mono text-[11px] text-[var(--rg-text)]">
              <span className="text-[var(--rg-text-strong)] font-bold">{u.abbreviation}</span>
              <span className="text-[var(--rg-muted)]">
                {" · "}
                {Array.isArray(u.changed_fields) ? u.changed_fields.join(", ") : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RevertSummaryBar({ summary }) {
  const chip = (label, n, color) => (
    <span
      key={label}
      className="inline-block px-2 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider mr-2 mb-2"
      style={{ color, border: `1px solid ${color}`, background: color + "15" }}
    >
      {n} {label}
    </span>
  );
  return (
    <div className="flex flex-wrap" data-testid="bulk-revert-summary">
      {chip("total", summary.total_rows, "var(--rg-muted)")}
      {chip("will revert", summary.will_revert, "var(--rg-sage)")}
      {summary.sot_conflicts > 0 && chip("SoT conflicts", summary.sot_conflicts, "var(--rg-amber)")}
      {summary.missing_rows > 0 && chip("missing", summary.missing_rows, "var(--rg-red)")}
    </div>
  );
}

function RevertRow({ plan }) {
  const isMissing = plan.action === "missing";
  const border = plan.sot_conflict ? "var(--rg-amber)" : (isMissing ? "var(--rg-red)" : "var(--rg-sage)");
  return (
    <div
      className="rounded-sm border p-3 bg-[var(--rg-surface)]"
      style={{ borderColor: border }}
      data-testid={`bulk-revert-row-${plan.abbreviation}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[var(--rg-text-strong)] font-semibold">{plan.abbreviation}</span>
        {isMissing && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-red)] font-bold border border-[var(--rg-red)] px-1.5 py-0.5 rounded-sm">
            Row missing — skipped
          </span>
        )}
        {plan.sot_conflict && (
          <span
            className="text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-bold border border-[var(--rg-amber)] px-1.5 py-0.5 rounded-sm"
            title="This row was edited manually AFTER the upload. Force mode required to overwrite."
          >
            SoT Conflict (edited after upload)
          </span>
        )}
      </div>
      {plan.reverts.length > 0 && (
        <ul className="space-y-1 text-[12px]">
          {plan.reverts.map((r, i) => (
            <li key={i} className="font-mono text-[var(--rg-text)]">
              <span className="text-[var(--rg-muted)]">{r.field}:</span>{" "}
              <span className="line-through text-[var(--rg-muted)]">{JSON.stringify(r.current)}</span>{" "}
              <span className="text-[var(--rg-sage)]">→</span>{" "}
              <span className="text-[var(--rg-text-strong)]">{JSON.stringify(r.restore)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] text-[var(--rg-muted)] font-mono mt-2">
        source_of_truth: {String(plan.current_sot)}{" "}
        <span className="text-[var(--rg-sage)]">→</span> {String(plan.previous_sot)}
      </div>
    </div>
  );
}
